import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Grid, Box, Button } from '@mui/material';
import { GeneralDatePicker, GeneralSelectMenu, GeneralTimePicker } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import { networkCreateRtmpApp, networkUpdateRtmpApp, useFetchRtmpAPPServerList } from 'src/network/rtmp-live';
import { useQueryClient } from '@tanstack/react-query';
import { RTMP_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const RtmpAppForm = () => {
  const queryClient = useQueryClient();
  const { modal, closeModal } = useFormsModalStore();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [rtmpAppServerList, setRtmpAppServerList] = useState([]);
  const [selectedRtmpAppServer, setSelectedRtmpAppServer] = useState('');

  const rtmpAppDetails = modal?.data;
  const isEditMode = !!rtmpAppDetails;

  const [startDate, setStartDate] = useState(
    rtmpAppDetails?.RtmpAppStartDateTime ? new Date(rtmpAppDetails?.RtmpAppStartDateTime) : new Date()
  );
  const [startTime, setStartTime] = useState(
    rtmpAppDetails?.RtmpAppStartDateTime ? new Date(rtmpAppDetails?.RtmpAppStartDateTime) : new Date()
  );

  const [endDate, setEndDate] = useState(
    rtmpAppDetails?.RtmpAppFinishDateTime ? new Date(rtmpAppDetails?.RtmpAppFinishDateTime) : new Date()
  );
  const [endTime, setEndTime] = useState(
    rtmpAppDetails?.RtmpAppFinishDateTime ? new Date(rtmpAppDetails?.RtmpAppFinishDateTime) : new Date()
  );

  const defaultValues = getDefaultValues(rtmpAppDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const startDateISO = new Date(startDate).toISOString();
    const startTimeISO = new Date(startTime).toISOString();
    const startDatePart = startDateISO.split('T')[0];
    const startTimePart = startTimeISO.split('T')[1];
    const finalStartDateTime = `${startDatePart}T${startTimePart}`;

    const endDateISO = new Date(endDate).toISOString();
    const endTimeISO = new Date(endTime).toISOString();
    const endDatePart = endDateISO.split('T')[0];
    const endTimePart = endTimeISO.split('T')[1];
    const finalEndDateTime = `${endDatePart}T${endTimePart}`;

    const PAYLOAD = {
      RtmpAppApplicationID: +projectId,
      RtmpAppLabel: values?.RtmpAppLabel,
      RtmpAppProfileID: -1,
      RtmpAppStartDateTime: finalStartDateTime,
      RtmpAppFinishDateTime: finalEndDateTime,
      RtmpAppRtmpAppServerID: selectedRtmpAppServer,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateRtmpApp(rtmpAppDetails.RtmpAppID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [RTMP_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateRtmpApp(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [RTMP_LIST] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }

      setSelectedRtmpAppServer(rtmpAppServerList[0]?.id);
    } catch (error) {
      console.error(error);
    }
  };

  const { data } = useFetchRtmpAPPServerList();

  useEffect(() => {
    if (data) {
      const rtmpAppServers = generateSelectMenuItems(data?.data?.items, 'RtmpAppServerLabel', 'RtmpAppServerID');
      setRtmpAppServerList(rtmpAppServers);
      setSelectedRtmpAppServer(rtmpAppServers?.RtmpAppServerID || rtmpAppServers?.[0]?.id);
    }
  }, [data]);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField name="RtmpAppLabel" type="text" label="عنوان" />
                <GeneralSelectMenu
                  label="نوع سرور"
                  name="RtmpAppRtmpAppServerID"
                  options={rtmpAppServerList}
                  value={selectedRtmpAppServer}
                  onChangeValue={setSelectedRtmpAppServer}
                />

                <GeneralDatePicker name="RtmpAppStartDateTime" label="تاریخ شروع" value={startDate} onChangeValue={setStartDate} />
                <GeneralTimePicker name="RtmpAppStartTime" label="ساعت شروع" value={startTime} onChangeValue={setStartTime} />

                <GeneralDatePicker name="RtmpAppFinishDateTime" label="تاریخ پایان" value={endDate} onChangeValue={setEndDate} />
                <GeneralTimePicker
                  minDate={startDate}
                  name="RtmpAppEndTime"
                  label="ساعت پایان"
                  value={endTime}
                  onChangeValue={setEndTime}
                />
              </Box>

              <div className="flex items-center gap-4 mt-60">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default RtmpAppForm;
